import React, { useState, useEffect } from "react";
import axiosInstance from "../config/axiosConfig";
import GdTable from "./components/GdTable";
import { formatMoney } from "../utils/utils";
import CartolaPacienteModal from "./components/CartolaPacienteModal";
/* global gdAlertify */

const CartolaPacientes = () => {
    // List and filter state
    const [pacientes, setPacientes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [periodo, setPeriodo] = useState(""); // YYYY-MM
    
    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [selectedPaciente, setSelectedPaciente] = useState(null);
    const [soloPendientes, setSoloPendientes] = useState(false);

    useEffect(() => {
        fetchPacientes();
    }, [periodo]);

    const fetchPacientes = async () => {
        setLoading(true);
        try {
            const url = periodo ? `/acuerdo-mutuo/cartola-pacientes/lista?periodo=${periodo}` : '/acuerdo-mutuo/cartola-pacientes/lista';
            const response = await axiosInstance.get(url);
            setPacientes(response.data.data || []);
        } catch (error) {
            console.error("Error al cargar pacientes para cartola", error);
            gdAlertify.notiError("Error al cargar la lista de cartolas.");
        } finally {
            setLoading(false);
        }
    };

    // Global calculations
    const globalCalculations = pacientes.reduce((acc, current) => {
        const liquidado = parseInt(current.total_liquidado || 0);
        const manual = parseInt(current.total_cargos_manuales || 0);
        const pagos = parseInt(current.total_pagos || 0);

        return {
            totalCargos: acc.totalCargos + liquidado + manual,
            totalAbonos: acc.totalAbonos + pagos,
            totalSaldo: acc.totalSaldo + (liquidado + manual - pagos)
        };
    }, { totalCargos: 0, totalAbonos: 0, totalSaldo: 0 });

    const nombreCompletoPaciente = row => [
        row.nombres,
        row.apellidoPaterno,
        row.apellidoMaterno
    ].filter(Boolean).join(" ");

    const columns = [
        {
            title: "RUT",
            name: "rut",
            width: "120px"
        },
        {
            title: "Nombre Paciente",
            name: "nombres",
            getFilterValue: nombreCompletoPaciente,
            render: nombreCompletoPaciente
        },
        {
            title: "Tutor de Pago",
            name: "tutor_nombre",
            width: "180px",
            getFilterValue: row => row.tutor_nombre || "—",
            render: row => <span className="small fw-bold">{row.tutor_nombre || "—"}</span>
        },
        {
            title: "Email Tutor",
            name: "tutor_correo",
            width: "180px",
            getFilterValue: row => row.tutor_correo || "—",
            render: row => <span className="small text-primary">{row.tutor_correo || "—"}</span>
        },
        {
            title: "Estado",
            name: "estado",
            width: "100px",
            getFilterValue: row => row.estado === 1 || row.estado === 'Activo' ? 'Activo' : 'Retirado',
            render: row => {
                const isActive = row.estado === 1 || row.estado === 'Activo';
                return (
                    <span className={`badge ${isActive ? 'bg-success' : 'bg-secondary'}`}>
                        {isActive ? 'Activo' : 'Retirado'}
                    </span>
                );
            }
        },
        {
            title: "T. Facturado",
            name: "total_liquidado",
            type: "number",
            cellClassName: "text-end",
            headerClassName: "text-end",
            getFilterValue: row => formatMoney(row.total_liquidado || 0),
            render: row => <span className="text-muted fw-bold">{formatMoney(row.total_liquidado || 0)}</span>
        },
        {
            title: "Cargos Manuales",
            name: "total_cargos_manuales",
            type: "number",
            cellClassName: "text-end",
            headerClassName: "text-end",
            getFilterValue: row => formatMoney(row.total_cargos_manuales || 0),
            render: row => <span className="text-warning text-dark fw-bold">{formatMoney(row.total_cargos_manuales || 0)}</span>
        },
        {
            title: "Abonos/Pagos",
            name: "total_pagos",
            type: "number",
            cellClassName: "text-end",
            headerClassName: "text-end",
            getFilterValue: row => formatMoney(row.total_pagos || 0),
            render: row => <span className="text-success fw-bold">{formatMoney(row.total_pagos || 0)}</span>
        },
        {
            title: "Saldo Actual",
            name: "saldo_consolidado",
            type: "number",
            cellClassName: "text-end",
            headerClassName: "text-end",
            getFilterValue: row => formatMoney(row.saldo_consolidado || 0),
            render: row => {
                const isDebt = row.saldo_consolidado > 0;
                return (
                    <span className={`fw-bold ${isDebt ? 'text-danger' : 'text-primary'}`}>
                        {formatMoney(row.saldo_consolidado || 0)}
                    </span>
                );
            }
        },
        {
            title: "Acciones",
            name: "acciones",
            width: "150px",
            filterable: false,
            render: row => (
                <button 
                    className="btn btn-sm btn-outline-primary fw-bold px-3 shadow-sm rounded-pill"
                    onClick={() => {
                        setSelectedPaciente(row);
                        setShowModal(true);
                    }}
                >
                    <i className="bi bi-eye me-1"></i> Ver
                </button>
            )
        }
    ];
    const pacientesFiltrados = pacientes.filter(p => {
        if (soloPendientes && p.saldo_consolidado <= 0) return false;
        return true;
    });

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-0 text-dark"><i className="bi bi-wallet2 text-primary me-2"></i>Cartola de Saldos por Paciente</h2>
                    <p className="text-muted small">Listado general de deuda, cargos adicionales y pagos por paciente. <b>{periodo ? `Filtrado por: ${periodo}` : "Mostrando Histórico"}</b></p>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center bg-white p-1 px-3 rounded-pill border shadow-sm">
                        <i className="bi bi-calendar3 me-2 text-primary"></i>
                        <input 
                            type="month" 
                            className="form-control form-control-sm border-0 bg-transparent shadow-none" 
                            value={periodo} 
                            onChange={(e) => setPeriodo(e.target.value)} 
                        />
                        {periodo && (
                            <button className="btn btn-link btn-sm text-danger p-0 ms-2" onClick={() => setPeriodo("")}>
                                <i className="bi bi-x-circle"></i>
                            </button>
                        )}
                    </div>
                    <div className="form-check form-switch bg-white p-2 px-3 rounded-pill border shadow-sm mb-0">
                        <input 
                            className="form-check-input ms-0 me-2" 
                            type="checkbox" 
                            id="switchPendientes" 
                            checked={soloPendientes}
                            onChange={(e) => setSoloPendientes(e.target.checked)}
                        />
                        <label className="form-check-label fw-bold text-dark small" htmlFor="switchPendientes">
                            Solo con Saldo
                        </label>
                    </div>
                    <button className="btn btn-light border shadow-sm rounded-pill px-3" onClick={fetchPacientes} disabled={loading}>
                        <i className={`bi ${loading ? "bi-hourglass-split" : "bi-arrow-clockwise"}`}></i>
                    </button>
                </div>
            </div>

            {/* Resumen Global */}
            <div className="row mb-4">
                <div className="col-md-4">
                    <div className="card shadow-sm border-0 border-start border-danger border-4">
                        <div className="card-body py-3 d-flex align-items-center justify-content-between">
                            <div>
                                <h6 className="text-muted fw-bold mb-1">Total Facturado & Cargos</h6>
                                <h4 className="mb-0 text-dark fw-bold">{formatMoney(globalCalculations.totalCargos)}</h4>
                            </div>
                            <div className="bg-danger bg-opacity-10 text-danger p-3 rounded-circle">
                                <i className="bi bi-receipt fs-4"></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card shadow-sm border-0 border-start border-success border-4">
                        <div className="card-body py-3 d-flex align-items-center justify-content-between">
                            <div>
                                <h6 className="text-muted fw-bold mb-1">Total Abonos Recibidos</h6>
                                <h4 className="mb-0 text-success fw-bold">{formatMoney(globalCalculations.totalAbonos)}</h4>
                            </div>
                            <div className="bg-success bg-opacity-10 text-success p-3 rounded-circle">
                                <i className="bi bi-cash-stack fs-4"></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card shadow-sm border-0 border-start border-primary border-4">
                        <div className="card-body py-3 d-flex align-items-center justify-content-between">
                            <div>
                                <h6 className="text-muted fw-bold mb-1">Saldo Total por Cobrar</h6>
                                <h4 className="mb-0 text-primary fw-bold">{formatMoney(globalCalculations.totalSaldo)}</h4>
                            </div>
                            <div className="bg-primary bg-opacity-10 text-primary p-3 rounded-circle">
                                <i className="bi bi-piggy-bank fs-4"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card shadow-sm border-0 rounded-4">
                <div className="card-body p-4 position-relative">
                    <GdTable
                        columns={columns}
                        data={pacientesFiltrados}
                        exportFilename="cartola-pacientes.xlsx"
                        loading={loading}
                    />
                </div>
            </div>

            {/* Modal for Cartola Details */}
            <CartolaPacienteModal 
                show={showModal} 
                handleClose={() => {
                    setShowModal(false);
                    setSelectedPaciente(null);
                    fetchPacientes(); // Refresh the general list on close to update balances.
                }} 
                paciente={selectedPaciente} 
            />
        </div>
    );
};

export default CartolaPacientes;
